import { ref, reactive, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import DataTransferService from '@/services/dataTransferService'
import { useUserStore } from '@/stores/userStore'
import { useCompanyStore } from '@/stores/companyStore'
import useLogger from '@/services/loggerService'
import useBlProviders from '@/composables/providers/useBlProvider'
import useCulture from '@/composables/staticData/useCulture'

import { groupedArrayByAttribute } from '@/helpers/arrayHelper'

const dataTransferService = DataTransferService()
const mapsDTService = dataTransferService.maps

export default function useMaps() {
	const {
		cultures,
	} = useCulture()
	const userStore = useUserStore()
	const {
		selectedCompanyId,
	} = storeToRefs(userStore)

	const companyStore = useCompanyStore()
	const { currentCompany } = storeToRefs(companyStore)

	const logger = useLogger()

	const {
		defaultBlFlags,
		getPreparedQueryForDt,
	} = useBlProviders()

	const { t: $t } = useI18n()

	const flags = reactive({
		...defaultBlFlags,
		isLoadingMapPreview: false,
		isLoadingRelatedFieldWorks: false,
	})

	let tracks = {
		telematicsLinesPathsSource: [],
		telematicsLinesStopsSource: [],
		telematicsEvents: [],
	}
	let tracksByMachineId = {
		telematicsLinesPathsSource: [],
		telematicsLinesStopsSource: [],
		telematicsEvents: [],
	}
	const tracksLastUpdated = ref(null)
	const tracksDateRange = ref({
		timeFrom: null,
		timeTo: null,
	})

	const track = ref(null)
	const chartData = ref(null)

	const extendedInfoFlags = reactive({
		...defaultBlFlags,
	})

	const extendedInfo = ref({})

	const movedMachinesFlags = reactive({
		...defaultBlFlags,
	})

	const movedMachines = ref({})

	const trackNullableObjects = computed(() => {
		return {
			id: -1,
			name: $t('Not specified'),
		}
	})

	const getTracks = (payload) => {
		// console.log('getTracks', payload)
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')
		// console.log('getTracks, preparedQuery', preparedQuery)
		const {
			time_from: dateTimeFrom,
			time_to: dateTimeTo,
		} = preparedQuery

		return new Promise((resolve, reject) => {
			flags.isLoading = true
			Promise.all([
				// dataTransferService.maps.getMapsTracks(selectedCompanyId.value, {...preparedQuery, machine_ids: [22080, 22103, 22079]}, dateTimeFrom, dateTimeTo, currentCompany.value.company_tz),
				dataTransferService.maps.getMapsTracks(selectedCompanyId.value, preparedQuery, dateTimeFrom, dateTimeTo, currentCompany.value.company_tz),

				// dataTransferService.maps.getMapsEvents(selectedCompanyId.value, {...preparedQuery, machine_ids: [22080, 22103, 22079]}),
				dataTransferService.maps.getMapsEvents(selectedCompanyId.value, preparedQuery),
			])
				.then(([telematicsLines, telematicsEvents]) => {
					// console.log('telematicsLines, telematicsEvents', telematicsLines, telematicsEvents);
					flags.isLoading = false
					tracks = {
						telematicsLinesPathsSource: telematicsLines.pathsSource,
						telematicsLinesStopsSource: telematicsLines.stopsSource,
						telematicsEvents,
					}
					tracksByMachineId = {
						telematicsLinesPathsSource: groupedArrayByAttribute(telematicsLines.pathsSource, 'machineId'),
						// telematicsLinesStopsSource: groupedArrayByAttribute(telematicsLines.stopsSource, 'machineId'),
						telematicsEvents: groupedArrayByAttribute([
							...telematicsEvents,
							...telematicsLines.stopsSource
						], 'machineId'),
					}
					tracksLastUpdated.value = new Date()
					tracksDateRange.value = {
						timeFrom: dateTimeFrom,
						timeTo: dateTimeTo,
					}
					// console.log('tracksByMachineId', tracksByMachineId)
					resolve(tracksByMachineId)
				})
				.catch((err) => {
					// console.error(err)
					flags.isLoading = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading tracks'),
					})
					reject(err)
				})
		})
	}

	const getTracksExtendedInfo = (payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')
		// const {
		// 	time_from: dateTimeFrom,
		// 	time_to: dateTimeTo,
		// } = preparedQuery
		return new Promise((resolve, reject) => {
			extendedInfoFlags.isLoading = true

			dataTransferService.maps.getMapsTracksExtendedInfo(
				selectedCompanyId.value,
				preparedQuery
			)
				.then((res) => {
					extendedInfoFlags.isLoading = false
					extendedInfo.value = res
					resolve(res)
				})
				.catch(() => {
					extendedInfoFlags.isLoading = false
					reject()
				})
		})
	}

	const getTracksMovedMachines = (payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')
		return new Promise((resolve, reject) => {
			movedMachinesFlags.isLoading = true

			dataTransferService.maps.getMapsTracksMovedMachines(
				selectedCompanyId.value,
				preparedQuery
			)
				.then((res) => {
					movedMachinesFlags.isLoading = false
					movedMachines.value = res
					resolve(res)
				})
				.catch(() => {
					movedMachinesFlags.isLoading = false
					reject()
				})
		})
	}

	const getTrack = (id, payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')

		return new Promise((resolve, reject) => {
			flags.isLoading = true
			dataTransferService.tracks
				.getTrack(selectedCompanyId.value, id, preparedQuery)
				.then((res) => {
					track.value = res
					flags.isLoading = false

					resolve(res)
				})
				.catch(() => {
					flags.isLoading = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading track'),
					})

					reject()
				})
		})
	}

	const getChartData = (machineId, payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to');

		return new Promise((resolve, reject) => {
			flags.isLoading = true

			dataTransferService.maps
				.getMapsTelematicChartData(selectedCompanyId.value, machineId, preparedQuery)
				.then((res) => {
					chartData.value = res
					flags.isLoading = false

					resolve(res)
				})
				.catch(() => {
					flags.isLoading = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading chart data'),
					})

					reject()
				})
		})
	}

	const movementPointsFlags = reactive({
		...defaultBlFlags,
	})

	let movementPoints = null

	const getMovementPoints = (payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')
		const {
			time_from: dateTimeFrom,
			time_to: dateTimeTo,
		} = preparedQuery
		// console.log('getMovementPoints, preparedQuery', preparedQuery)
		return new Promise((resolve, reject) => {
			movementPointsFlags.isLoading = true

			dataTransferService.maps.getMapsMovementPoints(
				selectedCompanyId.value,
				// {...preparedQuery, machine_ids: [22080, 22103, 22079]},
				preparedQuery,
				dateTimeFrom,
				dateTimeTo,
				currentCompany.value.company_tz
			)
				.then((res) => {
					movementPointsFlags.isLoading = false
					movementPoints = res
					resolve(res)
				})
				.catch((err) => {
					movementPointsFlags.isLoading = false
					reject(err)
				})
		})
	}

	const getTracksRealtimeData = () => {
		return new Promise((resolve, reject) => {
			mapsDTService.getMapsTracksRealtimeData(selectedCompanyId.value)
				.then((res) => {
					resolve(res)
				})
				.catch((err) => {
					reject(err)
				})
		})
	}


	const crops = ref([])
	const cropsFlags = reactive({
		...defaultBlFlags,
	})
	const getCrops = (payload) => {
		return new Promise((resolve, reject) => {
			cropsFlags.isLoading = true
			dataTransferService.maps
				.getMapsCrops(selectedCompanyId.value, payload)
				.then((res) => {
					const culturesIds = Object.keys(cultures.value)
					cropsFlags.isLoading = false
					crops.value = res.filter((crop) => culturesIds.includes(crop.culture.toString()))
					resolve(res)
				})
				.catch((err) => {
					cropsFlags.isLoading = false
					reject(err)
				})
		})
	}

	const cropsExtendedInfo = ref([])
	const cropsExtendedInfoFlags = reactive({
		...defaultBlFlags,
	})
	const getCropsExtendedInfo = (payload) => {
		return new Promise((resolve, reject) => {
			cropsExtendedInfoFlags.isLoading = true
			dataTransferService.maps
				.getMapsCropsExtendedInfo(selectedCompanyId.value, payload)
				.then((res) => {
					cropsExtendedInfoFlags.isLoading = false
					cropsExtendedInfo.value = res
					resolve(res)
				})
				.catch((err) => {
					cropsExtendedInfoFlags.isLoading = false
					reject(err)
				})
		})
	}

	const workOrdersInfoFlags = reactive({
		...defaultBlFlags,
	})
	const getFieldWorks = (payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')
		return new Promise((resolve, reject) => {
			workOrdersInfoFlags.isLoading = true
			dataTransferService.maps
				.getMapsFieldWorks(selectedCompanyId.value, preparedQuery)
				.then((res) => {
					workOrdersInfoFlags.isLoading = false
					const fact = []
					const plan = []
					const factGeo = {}
					const planGeo = {}
					res.fact.forEach((item) => {
						const copyItem = { ...item }
						delete copyItem.geo
						fact.push(copyItem)
						factGeo[item.id] = {
							id: item.id,
							...item.geo,
						}
					})
					res.plan.forEach((item) => {
						const copyItem = { ...item }
						delete copyItem.geo
						plan.push(copyItem)
						planGeo[item.id] = {
							id: item.id,
							...item.geo,
						}
					})
					// console.log('getFieldWorks', res)
					// console.log('fact', fact, factGeo)
					// console.log('plan', plan, planGeo)
					// console.log('-----')
					resolve({
						fact,
						plan,
						factGeo,
						planGeo,
					})
				})
				.catch((err) => {
					workOrdersInfoFlags.isLoading = false
					reject(err)
				})
		})
	}

	const logisticWorksFlags = reactive({
		...defaultBlFlags,
	})
	const getLogisticWorks = (payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')
		return new Promise((resolve, reject) => {
			logisticWorksFlags.isLoading = true
			mapsDTService.getMapsLogisticWorks(selectedCompanyId.value, preparedQuery)
				.then((res) => {
					logisticWorksFlags.isLoading = false
					const logistics = []
					const logisticsGeo = {}
					res.forEach((item) => {
						const copyItem = { ...item }
						delete copyItem.geo
						logistics.push(copyItem)
						logisticsGeo[item.id] = {
							id: item.id,
							...item.geo,
						}
					})
					// console.log('getLogisticWorks', res)
					// console.log('logistics', logistics, logisticsGeo)
					// console.log('-----')
					resolve({
						logistics,
						logisticsGeo,
					})
				})
				.catch((err) => {
					logisticWorksFlags.isLoading = false
					reject(err)
				})
		})
	}

	const mapsPreview = ref(null)
	const isShowMapsPreview = computed(() => flags.isLoadingMapPreview || mapsPreview.value && mapsPreview.value.feature_collection && mapsPreview.value.feature_collection.features.length > 0)
	const getMapsPreview = (payload) => {

		let map_info = {
			...payload,
		}

		if (payload.__ao__dateRange) {
			const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')

			map_info = {
				...map_info,
				time_from: preparedQuery.time_from,
				time_to: preparedQuery.time_to,
			}

			delete map_info.__ao__dateRange
		}

		flags.isLoadingMapPreview = true

		return new Promise((resolve, reject) => {
			dataTransferService.maps
				.getMapsPreview(selectedCompanyId.value, {
					map_info: JSON.stringify(map_info),
				})
				.then((res) => {
					mapsPreview.value = res
					flags.isLoadingMapPreview = false
					resolve(res)
				})
				.catch((err) => {
					flags.isLoadingMapPreview = false
					reject(err)
				})
		})
	}

	const machineTrack = ref(null)
	const machineTrackFlags = reactive({
		...defaultBlFlags,
	})
	const getMachineTrack = (machineId, payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')

		return new Promise((resolve, reject) => {
			machineTrackFlags.isLoading = true
			dataTransferService.maps
				.getMachineTrack(selectedCompanyId.value, machineId, preparedQuery)
				.then((res) => {
					machineTrack.value = res
					machineTrackFlags.isLoading = false
					resolve(res)
				})
				.catch((err) => {
					machineTrackFlags.isLoading = false
					reject(err)
				})
		})
	}

	const relatedFieldWorks = ref(null)
	const getRelatedFieldWorks = (payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')

		flags.isLoadingRelatedFieldWorks = true
		return new Promise((resolve, reject) => {
			dataTransferService.maps
				.getRelatedFieldWorks(selectedCompanyId.value, preparedQuery)
				.then((res) => {
					relatedFieldWorks.value = res
					resolve(res)
				})
				.catch((err) => {
					reject(err)
				})
				.finally(() => flags.isLoadingRelatedFieldWorks = false)
		})
	}

	const machineWideTrack = ref({})

	const getMachineWideTrack = (machineId, payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')

		return new Promise((resolve, reject) => {
			dataTransferService.maps
				.getMachineWideTrack(selectedCompanyId.value, machineId, preparedQuery)
				.then((res) => {
					machineWideTrack.value = res
					resolve(res)
				})
				.catch((err) => {
					reject(err)
				})
			})
		}

	const machineWideTrackCrossedCrops = ref({})

	const getMachineWideTrackCrossedCrops = (machineId, payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')

		return new Promise((resolve, reject) => {
			dataTransferService.maps
				.getMachineWideTrackCrossedCrops(selectedCompanyId.value, machineId, preparedQuery)
				.then((res) => {
					machineWideTrackCrossedCrops.value = res
					resolve(res)
				})
				.catch((err) => {
					reject(err)
				})
			}
		)
	}

	const machineTrackPoints = ref({})
	const machineTrackPointsFlags = reactive({
		...defaultBlFlags,
	})
	const getMachineTrackPoints = (machineId, payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')

		return new Promise((resolve, reject) => {
			machineTrackPointsFlags.isLoading = true
			dataTransferService.maps
				.getMachineTrackPoints(selectedCompanyId.value, machineId, preparedQuery)
				.then((res) => {
					machineTrackPoints.value = res
					machineTrackPointsFlags.isLoading = false
					resolve(res)
				})
				.catch((err) => {
					machineTrackPointsFlags.isLoading = false
					reject(err)
				})
			}
		)
	}

	const doneWorkGeometries = ref({})

	const getDoneWorkGeometries = (doneWorkId) => {
		return new Promise((resolve, reject) => {
			dataTransferService.maps
				.getDoneWorkGeometries(selectedCompanyId.value, doneWorkId)
				.then((res) => {
					doneWorkGeometries.value = res
					resolve(res)
				})
				.catch((err) => {
					reject(err)
				})
			}
		)
	}

	return {
		flags,
		tracks,
		tracksByMachineId,
		tracksLastUpdated,
		tracksDateRange,
		extendedInfoFlags,
		extendedInfo,
		movedMachinesFlags,
		movedMachines,
		track,
		trackNullableObjects,
		chartData,

		getTracks,
		getTracksExtendedInfo,
		getTracksMovedMachines,
		getTrack,
		getChartData,

		movementPointsFlags,
		movementPoints,
		getMovementPoints,
		getTracksRealtimeData,

		crops,
		cropsFlags,
		getCrops,

		cropsExtendedInfo,
		cropsExtendedInfoFlags,
		getCropsExtendedInfo,

		workOrdersInfoFlags,
		getFieldWorks,

		logisticWorksFlags,
		getLogisticWorks,

		isShowMapsPreview,
		mapsPreview,
		getMapsPreview,

		machineTrack,
		machineTrackFlags,
		getMachineTrack,

		relatedFieldWorks,
		getRelatedFieldWorks,

		machineWideTrack,
		getMachineWideTrack,

		machineWideTrackCrossedCrops,
		getMachineWideTrackCrossedCrops,

		machineTrackPoints,
		machineTrackPointsFlags,
		getMachineTrackPoints,

		doneWorkGeometries,
		getDoneWorkGeometries,
	}

}
